import { Warning } from '@mui/icons-material'
import { Box, Button, Tab, Tabs, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { mergeDeepRight } from 'ramda'
import { createContext, useEffect, useState } from 'react'
import { Form, FormRenderProps, FormSpy } from 'react-final-form'
import { logEvent } from '../../analyticsService'
import FormAPIHelper from './FormAPIHelper'

export const CustomValidationContext = createContext(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (validatorName: string, callback: () => Promise<boolean> | boolean) => {},
)

// TODO: better form theme typing
const StyledTab = styled(Tab, { shouldForwardProp: (prop) => prop !== 'isError' })<{
    isError: boolean
}>((props: any) => ({
    color: props.isError ? props.theme.warning : undefined,
}))
export interface IHandleSubmit {
    values: any
    form: FormRenderProps['form']
}

interface MultistepFormProps {
    formName: string // for telemetry purposes
    handleSubmit: (
        params: IHandleSubmit,
    ) => Record<string, string> | Promise<Record<string, string>>
    steps: {
        name: string
        error?: boolean
        component: JSX.Element
    }[]
    defaultState?: any
    lastStepIsResults?: boolean
    mutators?: any
    initialValues?: any
    // Pass in any values that are loaded async here and in initialValues
    // Pass in empty object / null; will be disabled if undefined
    initialRemoteValues?: any
    autoSave?: {
        enabled: boolean
        excludeFields?: string[]
        debounceTime: number
        handleSave: (payload: { [key: string]: any }) => void
    }
    forceEnableTabs?: boolean // Allow review of tabs if workflow fails after validation
    onSubmitForm?: () => void
    onSuccess?: (values: Record<string, any>) => void
    onFailure?: (errors: any) => void
    reset?: boolean
}

let debounce: NodeJS.Timeout | false = false

const MultistepForm = (props: MultistepFormProps) => {
    const [stepNumber, setStepNumber] = useState(0)
    const [formSubmitting, setFormSubmitting] = useState(false)
    const [formSubmitted, setFormSubmitted] = useState(false)
    const [formState, setFormState] = useState<{ [key: string]: any }>({
        ...props.initialValues,
        ...props.initialRemoteValues,
    })
    const [customValidators, setCustomValidators] = useState<Record<string, () => void>>({})

    useEffect(() => {
        setFormState(mergeDeepRight(formState, props.initialRemoteValues))
    }, [props.initialRemoteValues])

    const handleAutoSave = ({ values: newState }: any) => {
        if (debounce) clearTimeout(debounce)
        if (props.initialRemoteValues === undefined || !props.autoSave?.enabled) {
            return
        }

        debounce = setTimeout(() => {
            debounce = false
            const lastState = formState

            const changedValues: any = Object.keys({ ...lastState, ...newState }).reduce(
                (acc, key) => {
                    if (
                        props.autoSave?.excludeFields?.findIndex(
                            (excludedKey) => key === excludedKey,
                        ) !== -1
                    )
                        return acc
                    const lastValue = lastState[key]
                    const nextValue = newState[key]
                    if (lastValue !== nextValue) {
                        return { ...acc, [key]: nextValue === undefined ? null : nextValue }
                    }
                    return acc
                },
                {},
            )
            setFormState(newState)

            if (props.autoSave?.handleSave && Object.keys(changedValues).length) {
                props.autoSave.handleSave(changedValues)
            }
        }, props.autoSave?.debounceTime)
    }
    const renderFooter = (formProps: FormRenderProps) => {
        if (props.lastStepIsResults && stepNumber === props.steps.length - 1) return
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    gap: 2,
                    alignItems: 'center',
                    marginTop: 2,
                }}
            >
                <Typography variant="button">
                    Step {stepNumber + 1} of{' '}
                    {props.steps.length - (props.lastStepIsResults ? 1 : 0)}
                </Typography>
                <Button
                    sx={{ width: '100px' }}
                    onClick={() => setStepNumber(stepNumber - 1)}
                    disabled={stepNumber <= 0 || formProps.submitting}
                >
                    Back
                </Button>
                {stepNumber < props.steps.length - (props.lastStepIsResults ? 2 : 1) ? (
                    <Button
                        sx={{ width: '100px' }}
                        disabled={formProps.submitting}
                        onClick={() => {
                            logEvent('MS_FORM_NEXT_CLICK', {
                                formName: props.formName,
                                currentStep: stepNumber,
                            })
                            setStepNumber(stepNumber + 1)
                        }}
                    >
                        Next
                    </Button>
                ) : (
                    <Button
                        sx={{ width: '100px' }}
                        onClick={() => {
                            logEvent('MS_FORM_SUBMIT', {
                                formName: props.formName,
                                currentStep: stepNumber,
                            })
                            formProps.handleSubmit()
                            if (props.lastStepIsResults) setStepNumber(stepNumber + 1)
                        }}
                        disabled={formProps.submitting}
                    >
                        Submit
                    </Button>
                )}
            </Box>
        )
    }

    const tabIsDisabled = (index: number) => {
        if (props.lastStepIsResults && index === props.steps.length - 1) return true
        if (props.forceEnableTabs) return false
        return formSubmitting || formSubmitted
    }

    return (
        <Box>
            <Box sx={{ marginTop: '30px', borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={stepNumber}
                    scrollButtons="auto"
                    variant="scrollable"
                    onChange={(_event, value) => setStepNumber(value)}
                >
                    {props.steps.map(({ name, error }, i) => (
                        <StyledTab
                            isError={error ?? false}
                            iconPosition="start"
                            icon={error ? <Warning /> : undefined}
                            key={name}
                            label={name}
                            disabled={tabIsDisabled(i)}
                        />
                    ))}
                </Tabs>
            </Box>
            <Box>
                <Form
                    onSubmit={async (values, form) => {
                        props.onSubmitForm && props.onSubmitForm()
                        const parentValidationErrors = await props.handleSubmit({ values, form })

                        // Pretty special cases only
                        const childrenValidationErrors = await Promise.all(
                            Object.keys(customValidators).map(async (validatorKey) => {
                                const validator = customValidators[validatorKey]
                                const isValid = await validator()
                                return [validatorKey, isValid]
                            }),
                        )

                        const errors = {
                            ...parentValidationErrors,
                            ...childrenValidationErrors.reduce(
                                (acc, [key, isValid]) =>
                                    isValid
                                        ? acc
                                        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                          { ...acc, [key!]: 'Internal validation failed' },
                                {},
                            ),
                        }

                        if (Object.keys(errors).length) {
                            props.onFailure && props.onFailure(errors)
                        } else {
                            setFormSubmitted(true)
                            props.onSuccess && props.onSuccess(values)
                        }

                        if (props.lastStepIsResults) {
                            setStepNumber(stepNumber + 1)
                        }
                        return errors
                    }}
                    mutators={props.mutators}
                    initialValues={formState}
                    render={(formProps) => (
                        <CustomValidationContext.Provider
                            value={(
                                validatorName: string,
                                callback: () => Promise<boolean> | boolean,
                            ) => {
                                if (customValidators[validatorName]) return
                                setCustomValidators({
                                    ...customValidators,
                                    [validatorName]: callback,
                                })
                            }}
                        >
                            <FormAPIHelper shouldReset={props.reset} />
                            <FormSpy subscription={{ values: true }} onChange={handleAutoSave} />
                            <FormSpy
                                subscription={{ submitting: true }}
                                onChange={(formState) => {
                                    setFormSubmitting(formState.submitting)
                                }}
                            />
                            {props.steps.map(({ component }, i) => (
                                <div
                                    key={i}
                                    style={{
                                        ...(stepNumber === i ? undefined : { display: 'none' }),
                                        paddingTop: '16px',
                                    }}
                                >
                                    {component}
                                </div>
                            ))}
                            {renderFooter(formProps)}
                        </CustomValidationContext.Provider>
                    )}
                />
            </Box>
        </Box>
    )
}

export default MultistepForm
